import { ProfileView, Visibility, ArticlePreviewResponse } from "@authory-packages/core";
import { theme } from "../../theme/theme";
import { VerticalSpacing } from "../VerticalSpacing";
import {
    ItemImageWrapper,
    ItemTextWrapper,
    PlIListThumb,
    PLIThumbnail,
    PLIThumbnailImage,
    PLIThumbnailPad,
    PLIViewVisibility,
    PLIWrapper,
    V3FeedDescription,
    V3FeedTitle
} from "./V3ProfileListItem.styles";
import { getCollapseBreakpoint, getDescNumLines, getTitleNumLines } from "./V3ProfileListItem.utils";
import { V3ProfileListItemLink } from "./V3ProfileListItemLink";
import { ItemMetadata } from "../ItemMetadata";
import { AuthoryColorSchemes } from "../ProfileHero/V3ProfileHero.types";

export interface V3ProfileListItemProps {
    item: ArticlePreviewResponse;
    viewType: ProfileView;
    isPinned?: boolean;
    href: string;
    isPrivateView?: boolean;
    isSignupPreview?: boolean;
    hideContentType?: boolean;
    hideFavicon?: boolean;
    responsiveBreakpoint?: number;
    onItemClick?: (id: string) => void;
}

export const V3ProfileListItem = ({
    item,
    viewType = ProfileView.TwoColumnThumbnail,
    isPinned,
    href,
    isPrivateView,
    hideFavicon,
    hideContentType,
    isSignupPreview = false,
    responsiveBreakpoint,
    onItemClick
}: V3ProfileListItemProps) => {
    const target = item.effectiveVisibility === Visibility.Preview ? "_blank" : undefined;

    // Views
    const isTwoColumn = viewType === ProfileView.TwoColumnThumbnail;
    const isText = viewType === ProfileView.Text;
    const isList = viewType === ProfileView.List;
    const isMasonry = viewType === ProfileView.Masonry;

    const breakpoint = getCollapseBreakpoint(viewType, responsiveBreakpoint);
    const mobileView = breakpoint ? ProfileView.OneColumnThumbnail : viewType;

    const hasImage = !!item.previewImage && !!item.thumbnailImage;
    const hasDescription = !!item.description || !!item.ogDescription;
    const hasTitle = !!item.title || !!item.ogTitle;

    const showDesc = hasDescription || isSignupPreview;
    const descHiddenViews = isTwoColumn && hasImage && hasTitle ? [ProfileView.TwoColumnThumbnail] : [];

    const onClickCallback = (e?: React.MouseEvent) => {
        if (e && e.button !== 0 && e.button !== 1) return;
        onItemClick && item.canonicalSlug && onItemClick(item.canonicalSlug);
    };

    return (
        <PLIWrapper as="article" $dash={isText} $isPinned={isPinned} $viewType={viewType} $breakpoint={breakpoint}>
            {hasImage && (!isList || !!breakpoint) && (
                <ItemImageWrapper $viewType={viewType} $breakpoint={breakpoint} $isPinned={isPinned}>
                    <V3ProfileListItemLink href={href} target={target} onClick={onClickCallback} onAuxClick={onClickCallback} decorative>
                        {!isText && (
                            <PLIThumbnailPad $viewType={viewType} $breakpoint={breakpoint} $isPinned={isPinned}>
                                <PLIThumbnail
                                    $viewType={viewType}
                                    $breakpoint={breakpoint}
                                    $isPinned={isPinned}
                                    $imageRatio={
                                        item.previewImageWidth && item.previewImageHeight
                                            ? `${(item.previewImageHeight / item.previewImageWidth) * 100}%`
                                            : undefined
                                    }
                                >
                                    <picture>
                                        {isTwoColumn && breakpoint && item.thumbnailImage && (
                                            <source media={`(min-width: ${breakpoint}px)`} srcSet={item.thumbnailImage} />
                                        )}
                                        <PLIThumbnailImage
                                            src={(isTwoColumn && !breakpoint ? item.thumbnailImage : item.previewImage) ?? undefined}
                                            alt={item.ogTitle || item.title || ""}
                                            loading="lazy"
                                            decoding="async"
                                            $contain={isMasonry}
                                        />
                                    </picture>
                                </PLIThumbnail>
                            </PLIThumbnailPad>
                        )}
                    </V3ProfileListItemLink>
                </ItemImageWrapper>
            )}
            <ItemTextWrapper $isPinned={isPinned} $viewType={viewType} $breakpoint={breakpoint}>
                <div>
                    <V3ProfileListItemLink href={href} target={target} onClick={onClickCallback} onAuxClick={onClickCallback}>
                        {hasTitle && (
                            <VerticalSpacing bottom={theme.v3.spacings.spacing3}>
                                <V3FeedTitle
                                    as="h2"
                                    $numLines={getTitleNumLines(mobileView, hasImage, hasDescription)}
                                    $desktopNumLines={getTitleNumLines(viewType, hasImage, hasDescription)}
                                    $breakpoint={breakpoint}
                                    $colorVariant={AuthoryColorSchemes.userTheme}
                                >
                                    {item.ogTitle || item.title}
                                </V3FeedTitle>
                            </VerticalSpacing>
                        )}
                        {showDesc && (
                            <PLIViewVisibility
                                bottom={theme.v3.spacings.spacing3}
                                $viewType={viewType}
                                $breakpoint={breakpoint}
                                $hiddenViews={descHiddenViews}
                            >
                                <V3FeedDescription
                                    $numLines={getDescNumLines(mobileView, hasImage, hasTitle)}
                                    $desktopNumLines={getDescNumLines(viewType, hasImage, hasTitle)}
                                    $breakpoint={breakpoint}
                                    $colorVariant={AuthoryColorSchemes.userTheme}
                                >
                                    {item.ogDescription || item.description}{" "}
                                </V3FeedDescription>
                            </PLIViewVisibility>
                        )}
                    </V3ProfileListItemLink>
                    <ItemMetadata
                        item={item}
                        customDateFormat={isMasonry ? "MMM dd, yyyy" : undefined}
                        compactDateFormat={isMasonry ? undefined : "MMM dd yy"}
                        isPrivateView={isPrivateView}
                        showRedirect={item.effectiveVisibility === Visibility.Preview && !isSignupPreview}
                        hideFavicon={hideFavicon}
                        hideContentType={hideContentType}
                        allowUserThemable
                    />
                </div>
            </ItemTextWrapper>
            {isList && hasImage && (
                <PlIListThumb $breakpoint={breakpoint}>
                    <V3ProfileListItemLink href={href} target={target} onClick={onClickCallback} onAuxClick={onClickCallback} decorative>
                        <PLIThumbnail $viewType={viewType} $isPinned={isPinned}>
                            <PLIThumbnailImage
                                src={item.thumbnailImage ?? undefined}
                                alt={item.ogTitle || item.title || ""}
                                loading="lazy"
                                decoding="async"
                                $contain={false}
                            />
                        </PLIThumbnail>
                    </V3ProfileListItemLink>
                </PlIListThumb>
            )}
        </PLIWrapper>
    );
};
